/**
 * ISSUE-1164. The player page's server reads: the team roster for the
 * player's record and headshot, and the team injury report for status. Both
 * run only when the URL names a team. A box score link carries the sports-api
 * playerId when events-api resolved the row's vendor reference on that team
 * (ISSUE-1164 revision 2). Otherwise it carries a minted id, and
 * findRosterPlayer falls back to an exact, unique full name inside that one
 * roster, so an abbreviated name such as N. Jokic shows initials instead of a
 * guess. A failed read opens the shared error modal and the page keeps its URL
 * values.
 */
import {
  useTeamInjuriesGet,
  useTeamRosterGet,
} from "@sportsmet/sports-sdk/private";
import { useEffect } from "react";

import { extractErrorCode, showApiError } from "../../errors/apiErrorBus";
import {
  findRosterPlayer,
  resolvePlayerInjuryStatus,
  type typePlayerProfileDetails,
  type typePlayerProfileView,
} from "./playerProfileReader";

export function usePlayerProfileData(
  view: typePlayerProfileView | null,
): typePlayerProfileDetails {
  const teamId = view?.teamId ?? null;
  const roster = useTeamRosterGet(
    { teamId: teamId ?? "" },
    { enabled: teamId !== null },
  );
  const injuries = useTeamInjuriesGet(
    { teamId: teamId ?? "" },
    { enabled: teamId !== null },
  );

  // roster.data and injuries.data are deliberately left out of the dependency
  // lists: each effect should fire once per new answer, keyed by its
  // dataUpdatedAt and the team currently shown, not on every render that holds
  // the same data.
  useEffect(() => {
    if (extractErrorCode(roster.data) !== null) {
      showApiError(roster.data, undefined, roster.dataUpdatedAt);
    }
  }, [roster.dataUpdatedAt, teamId]);

  useEffect(() => {
    if (extractErrorCode(injuries.data) !== null) {
      showApiError(injuries.data, undefined, injuries.dataUpdatedAt);
    }
  }, [injuries.dataUpdatedAt, teamId]);

  if (view === null) {
    return {
      record: null,
      injuryStatus: null,
      hasInjuryReport: false,
      isLoading: false,
    };
  }

  const record = findRosterPlayer({
    groups: roster.data?.data?.groups ?? [],
    playerId: view.playerId,
    name: view.name,
  });
  const injuryStatus = resolvePlayerInjuryStatus({
    fromSearch: view.injuryStatus,
    injuries: injuries.data?.data?.injuries ?? [],
    playerIds:
      record === null ? [view.playerId] : [view.playerId, record.playerId],
  });

  return {
    record,
    injuryStatus,
    hasInjuryReport: injuries.data?.data?.hasReport === true,
    isLoading: roster.isLoading || injuries.isLoading,
  };
}
